import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2, Users } from "lucide-react";
import type { PartnerReferrer } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";

interface PartnerReferrersPanelProps {
  partnerId: string;
}

interface ReferrerFormData {
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  mobilePhone: string;
  officePhone: string;
  isPrimaryContact: boolean;
  isActive: boolean;
  notes: string;
}

const defaultFormData: ReferrerFormData = {
  firstName: "",
  lastName: "",
  position: "",
  email: "",
  mobilePhone: "",
  officePhone: "",
  isPrimaryContact: false,
  isActive: true,
  notes: "",
};

export function PartnerReferrersPanel({ partnerId }: PartnerReferrersPanelProps) {
  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [editingReferrer, setEditingReferrer] = useState<PartnerReferrer | null>(null);
  const [formData, setFormData] = useState<ReferrerFormData>(defaultFormData);
  const [deleteReferrer, setDeleteReferrer] = useState<PartnerReferrer | null>(null);
  const [formErrors, setFormErrors] = useState<{ firstName?: string; lastName?: string }>({});
  const { toast } = useToast();

  // Fetch referrers (uses default queryFn with auth)
  const { data: referrers = [], isLoading } = useQuery<PartnerReferrer[]>({
    queryKey: ["/api/crm/partners", partnerId, "referrers"],
  });

  // Sort referrers: primary contacts first
  const sortedReferrers = [...referrers].sort((a, b) => {
    if (a.isPrimaryContact && !b.isPrimaryContact) return -1;
    if (!a.isPrimaryContact && b.isPrimaryContact) return 1;
    return 0;
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: Partial<ReferrerFormData>) => {
      const response = await apiRequest("POST", `/api/crm/partners/${partnerId}/referrers`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/partners/${partnerId}/referrers`] });
      toast({ title: "Referrer added successfully" });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add referrer",
        variant: "destructive",
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ReferrerFormData> }) => {
      const response = await apiRequest("PATCH", `/api/crm/partner-referrers/${id}`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/partners/${partnerId}/referrers`] });
      toast({ title: "Referrer updated successfully" });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update referrer",
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/partner-referrers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/partners/${partnerId}/referrers`] });
      toast({ title: "Referrer deleted" });
      setDeleteReferrer(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete referrer",
        variant: "destructive",
      });
    },
  });

  const handleCloseDialog = () => {
    setIsAddEditOpen(false);
    setEditingReferrer(null);
    setFormData(defaultFormData);
    setFormErrors({});
  };

  const handleOpenAdd = () => {
    setEditingReferrer(null);
    setFormData(defaultFormData);
    setFormErrors({});
    setIsAddEditOpen(true);
  };

  const handleOpenEdit = (referrer: PartnerReferrer) => {
    setEditingReferrer(referrer);
    setFormData({
      firstName: referrer.firstName,
      lastName: referrer.lastName,
      position: referrer.position || "",
      email: referrer.email || "",
      mobilePhone: referrer.mobilePhone || "",
      officePhone: referrer.officePhone || "",
      isPrimaryContact: referrer.isPrimaryContact || false,
      isActive: referrer.isActive ?? true,
      notes: referrer.notes || "",
    });
    setFormErrors({});
    setIsAddEditOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: { firstName?: string; lastName?: string } = {};
    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const submitData: Partial<ReferrerFormData> = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      position: formData.position.trim() || undefined,
      email: formData.email.trim() || undefined,
      mobilePhone: formData.mobilePhone.trim() || undefined,
      officePhone: formData.officePhone.trim() || undefined,
      isPrimaryContact: formData.isPrimaryContact,
      isActive: formData.isActive,
      notes: formData.notes.trim() || undefined,
    };

    // Clean undefined values
    const cleanedData = Object.fromEntries(
      Object.entries(submitData).filter(([_, v]) => v !== undefined)
    );

    if (editingReferrer) {
      updateMutation.mutate({ id: editingReferrer.id, data: cleanedData });
    } else {
      createMutation.mutate(cleanedData);
    }
  };

  const isFormValid = formData.firstName.trim() !== "" && formData.lastName.trim() !== "";
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="panel-partner-referrers">
      {/* Panel Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground">
          REFERRER CONTACTS ({referrers.length})
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={handleOpenAdd}
          data-testid="btn-add-referrer"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Referrer
        </Button>
      </div>

      {/* Empty State */}
      {referrers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md">
          <Users className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No referrer contacts added</p>
          <p className="text-xs text-muted-foreground mt-1">
            Add individual contacts who refer clients from this partner
          </p>
        </div>
      ) : (
        /* Referrers Table */
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="h-10 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-1">Name</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Position</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Contact</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Primary</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Status</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedReferrers.map((referrer) => (
                <TableRow key={referrer.id} className="h-10">
                  <TableCell className="px-2 py-1 text-sm">
                    {referrer.firstName} {referrer.lastName}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {referrer.position || "-"}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    <div className="flex flex-col">
                      {referrer.email && <span>{referrer.email}</span>}
                      {referrer.mobilePhone && (
                        <span className="text-muted-foreground text-xs">
                          {referrer.mobilePhone}
                        </span>
                      )}
                      {!referrer.email && !referrer.mobilePhone && "-"}
                    </div>
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    {referrer.isPrimaryContact && (
                      <Badge variant="default" data-testid="badge-primary">
                        Primary
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <Badge variant={referrer.isActive ? "default" : "secondary"}>
                      {referrer.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEdit(referrer)}
                        data-testid={`btn-edit-referrer-${referrer.id}`}
                        aria-label="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteReferrer(referrer)}
                        data-testid={`btn-delete-referrer-${referrer.id}`}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isAddEditOpen} onOpenChange={setIsAddEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingReferrer ? "Edit Referrer Contact" : "Add Referrer Contact"}
            </DialogTitle>
            <DialogDescription>
              {editingReferrer
                ? "Update the referrer contact details."
                : "Add a new referrer contact for this partner."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="Enter first name"
              />
              {formErrors.firstName && (
                <p className="text-sm text-destructive">{formErrors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Enter last name"
              />
              {formErrors.lastName && (
                <p className="text-sm text-destructive">{formErrors.lastName}</p>
              )}
            </div>

            {/* Position */}
            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <Input
                id="position"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                placeholder="e.g., Account Manager"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>

            {/* Mobile Phone */}
            <div className="space-y-2">
              <Label htmlFor="mobilePhone">Mobile Phone</Label>
              <Input
                id="mobilePhone"
                value={formData.mobilePhone}
                onChange={(e) => setFormData({ ...formData, mobilePhone: e.target.value })}
                placeholder="0412 345 678"
              />
            </div>

            {/* Office Phone */}
            <div className="space-y-2">
              <Label htmlFor="officePhone">Office Phone</Label>
              <Input
                id="officePhone"
                value={formData.officePhone}
                onChange={(e) => setFormData({ ...formData, officePhone: e.target.value })}
                placeholder="02 1234 5678"
              />
            </div>

            {/* Checkboxes */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isPrimaryContact"
                  checked={formData.isPrimaryContact}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isPrimaryContact: checked === true })
                  }
                />
                <Label htmlFor="isPrimaryContact" className="text-sm font-normal">
                  Primary Contact
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked === true })
                  }
                />
                <Label htmlFor="isActive" className="text-sm font-normal">
                  Active
                </Label>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this referrer..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <span
              onClick={() => {
                if (!isFormValid && !isSubmitting) {
                  validateForm();
                }
              }}
              style={{ display: 'inline-block' }}
            >
              <Button
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save
              </Button>
            </span>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteReferrer}
        onOpenChange={(open) => !open && setDeleteReferrer(null)}
      >
        <AlertDialogContent data-testid="dialog-delete-referrer">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Referrer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteReferrer?.firstName} {deleteReferrer?.lastName}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteReferrer && deleteMutation.mutate(deleteReferrer.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
